import React, { useState } from 'react';
import { X, Check, Globe, Clock, DollarSign, Navigation, AlertTriangle, Sparkles, CheckCircle2, Truck, Pause, Building } from 'lucide-react';

function timeStringToSec(timeStr = '08:00') {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(':').map(Number);
  return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60;
}

function secondsToTimeString(totalSec = 0) {
  const finalH = Math.floor((totalSec / 3600) % 24);
  const finalM = Math.floor((totalSec % 3600) / 60);
  return `${String(finalH).padStart(2, '0')}:${String(finalM).padStart(2, '0')}`;
}

function getStopWindowStartSec(stop) {
  if (!stop) return null;
  if (stop.time_windows) {
    let tws = stop.time_windows;
    if (typeof tws === 'string' && tws.trim().length > 0) {
      try { tws = JSON.parse(tws); } catch {}
    }
    if (Array.isArray(tws) && tws.length > 0 && tws[0].start) {
      return timeStringToSec(tws[0].start);
    }
  }
  if (stop.time_window_start) {
    return timeStringToSec(stop.time_window_start);
  }
  return null;
}

function calcDepartureTimeStr(arrivalStr = '08:00', serviceMins = 10, stop = null) {
  if (!arrivalStr) return '08:10';
  const arrSec = timeStringToSec(arrivalStr);
  const windowStartSec = getStopWindowStartSec(stop);
  const serviceStartSec = (windowStartSec !== null && arrSec < windowStartSec) ? windowStartSec : arrSec;
  const depSec = serviceStartSec + (parseInt(serviceMins || 10) * 60);
  return secondsToTimeString(depSec);
}

function formatSecToHoursMins(sec = 0) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function calcHaversineDistanceKm(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
}

function renderStopRealTimeWindows(stop, arrivalTimeFormatted = null) {
  let stopTimeWindows = [];
  if (Array.isArray(stop?.time_windows) && stop.time_windows.length > 0) {
    stopTimeWindows = stop.time_windows.map(tw => ({
      start: tw.start ? tw.start.slice(0, 5) : '08:00',
      end: tw.end ? tw.end.slice(0, 5) : '18:00'
    }));
  } else if (stop?.time_window_start) {
    stopTimeWindows = [{
      start: stop.time_window_start.slice(0, 5),
      end: (stop.time_window_end || '18:00').slice(0, 5)
    }];
  } else {
    stopTimeWindows = [{ start: '08:00', end: '18:00' }];
  }

  const arrSec = arrivalTimeFormatted ? timeStringToSec(arrivalTimeFormatted) : null;

  let isAnyHit = false;
  if (arrSec !== null) {
    isAnyHit = stopTimeWindows.some(tw => {
      const sSec = timeStringToSec(tw.start);
      const eSec = timeStringToSec(tw.end);
      return arrSec >= sSec && arrSec <= eSec;
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center', justifyContent: 'center' }}>
      {stopTimeWindows.map((tw, i) => {
        let isHit = false;
        if (arrSec !== null) {
          const sSec = timeStringToSec(tw.start);
          const eSec = timeStringToSec(tw.end);
          isHit = arrSec >= sSec && arrSec <= eSec;
        }

        let bg = 'rgba(245, 158, 11, 0.15)';
        let color = '#f59e0b';
        let border = '1px solid rgba(245, 158, 11, 0.3)';

        if (arrSec !== null) {
          if (isHit) {
            bg = 'rgba(34, 197, 94, 0.2)';
            color = '#22c55e';
            border = '1px solid #166534';
          } else if (isAnyHit) {
            bg = '#0f172a';
            color = '#64748b';
            border = '1px solid #334155';
          } else {
            bg = 'rgba(239, 68, 68, 0.25)';
            color = '#ef4444';
            border = '1px solid #991b1b';
          }
        }

        return (
          <span
            key={i}
            style={{
              background: bg,
              color: color,
              border: border,
              borderRadius: '4px',
              padding: '2px 5px',
              fontSize: '0.65rem',
              fontWeight: '700',
              letterSpacing: '0.3px',
              whiteSpace: 'nowrap'
            }}
          >
            {tw.start} - {tw.end}
          </span>
        );
      })}
    </div>
  );
}

function SingleRouteCard({
  title,
  job,
  stops = [],
  pauses = [],
  startHub,
  endHub,
  isOptimized = false
}) {
  const lengthKm = job.estimated_length_km ? Number(job.estimated_length_km).toFixed(1) : '0.0';
  const durationStr = job.estimated_duration_sec ? formatSecToHoursMins(Number(job.estimated_duration_sec)) : '0 min';
  const costVal = job.estimated_cost ? Number(job.estimated_cost).toFixed(2) : '0.00';

  const startTime = (job.estimated_departure || '07:30').slice(0, 5);
  const startHubName = startHub?.name || 'Hub Principal';
  const endHubName = endHub?.name || 'Hub Principal';

  let returnTimeStr = startTime;
  if (job?.estimated_duration_sec) {
    returnTimeStr = secondsToTimeString(timeStringToSec(startTime) + Number(job.estimated_duration_sec));
  } else if (stops.length > 0) {
    const lastStop = stops[stops.length - 1];
    const lastArr = lastStop.estimated_arrival ? lastStop.estimated_arrival.slice(0, 5) : '17:00';
    const lastService = parseInt(lastStop.service_time_mins || 10);
    const lastDepSec = timeStringToSec(lastArr) + (lastService * 60);
    const lastKm = calcHaversineDistanceKm(lastStop.latitude, lastStop.longitude, endHub?.latitude, endHub?.longitude);
    const returnDriveSec = Math.max(1, Math.round((lastKm / 40) * 3600));
    returnTimeStr = secondsToTimeString(lastDepSec + returnDriveSec);
  }

  return (
    <div style={{
      flex: 1,
      background: '#1e293b',
      border: isOptimized ? '1px solid #22c55e' : '1px solid #334155',
      borderRadius: '10px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: isOptimized ? '0 0 12px rgba(34, 197, 94, 0.15)' : 'none'
    }}>
      {/* HEADER BANNER */}
      <div style={{
        background: isOptimized ? 'rgba(34, 197, 94, 0.15)' : '#0f172a',
        padding: '10px 14px',
        borderBottom: isOptimized ? '1px solid #166534' : '1px solid #334155',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: '800', color: isOptimized ? '#22c55e' : '#f8fafc' }}>
            {isOptimized ? 'Optimisé' : 'Actuel'}
          </span>
        </div>

        {/* STATS RIGHT-ALIGNED IDENTICAL TO MAIN VIEW */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginLeft: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Distance estimée">
            <Navigation size={13} color="#22c55e" />
            <span style={{ color: '#cbd5e1', fontWeight: '700', fontSize: '0.78rem' }}>{lengthKm} km</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Durée estimée">
            <Clock size={13} color="#38bdf8" />
            <span style={{ color: '#cbd5e1', fontWeight: '700', fontSize: '0.78rem' }}>{durationStr}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Coût estimé">
            <DollarSign size={13} color="#ef4444" />
            <span style={{ color: '#cbd5e1', fontWeight: '700', fontSize: '0.78rem' }}>{costVal} CHF</span>
          </div>
        </div>
      </div>

      {/* DÉPART BLOCK */}
      <div style={{ background: '#0f172a', padding: '6px 10px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: '#94a3b8' }}>
        <Building size={13} color="#94a3b8" />
        <span>Départ :</span>
        <span style={{ fontSize: '0.72rem', color: '#ffffff', fontWeight: '700' }}>{startTime}</span>
        <span style={{ padding: '2px 6px', fontSize: '0.72rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', borderRadius: '4px', marginLeft: 'auto' }}>
          {startHubName}
        </span>
      </div>

      {/* TRAJET 1: START HUB TO FIRST STOP */}
      {stops.length > 0 && (() => {
        const firstStop = stops[0];
        const km = firstStop.leg_distance_km !== undefined
          ? firstStop.leg_distance_km
          : calcHaversineDistanceKm(startHub?.latitude, startHub?.longitude, firstStop.latitude, firstStop.longitude);
        let mins = firstStop.leg_drive_mins !== undefined
          ? firstStop.leg_drive_mins
          : Math.max(1, Math.round((km / 40) * 60));
        if (firstStop.leg_drive_mins === undefined && firstStop.estimated_arrival) {
          const startSec = timeStringToSec(startTime);
          const arrSec = timeStringToSec(firstStop.estimated_arrival);
          // First stop's pauses (if any) are already included in arrSec, so subtract them
          const firstPauses = (pauses || []).filter(p => p.before_stop_id === firstStop.id || parseInt(p.before_stop_id) === parseInt(firstStop.id));
          const firstPauseSec = firstPauses.reduce((sum, p) => sum + parseInt(p.duration_mins || p.estimated_length_mins || 10) * 60, 0);
          const driveSec = arrSec - firstPauseSec - startSec;
          if (driveSec > 0) {
            mins = Math.max(1, Math.round(driveSec / 60));
          }
        }
        return (
          <div style={{ background: '#0f172a', padding: '4px 10px', borderBottom: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'left' }}>
            <Truck size={13} color="#94a3b8" />
            <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{mins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{km} km</span></span>
          </div>
        );
      })()}

      {/* STOPS TABLE */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0', maxHeight: '42vh' }}>
        <table className="data-table" style={{ background: 'transparent', border: 'none', width: '100%' }}>
          <tbody>
            {stops.map((stop, idx) => {
              const arrivalTimeFormatted = stop.estimated_arrival ? stop.estimated_arrival.slice(0,5) : '08:00';
              const serviceMins = parseInt(stop.service_time_mins || 10);
              const departureTimeFormatted = calcDepartureTimeStr(arrivalTimeFormatted, serviceMins, stop);
              const pausesForStop = (pauses || []).filter(p => p.before_stop_id === stop.id || parseInt(p.before_stop_id) === parseInt(stop.id));

              return (
                <React.Fragment key={stop.id || idx}>
                  {pausesForStop.map((p, pIdx) => {
                    const dur = p.duration_mins || p.estimated_length_mins || 10;
                    const timeWindowStr = p.start && p.end ? ` (${p.start} - ${p.end})` : '';
                    return (
                      <tr key={`pause-${p.id || pIdx}`} style={{ background: '#0f172a' }}>
                        <td colSpan={5} style={{ padding: '4px 10px', borderTop: '1px dashed #334155', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8', fontWeight: '400' }}>
                            <Pause size={13} color="#94a3b8" />
                            <span>Pause : {dur} min{timeWindowStr}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}>
                    {/* COL 0: STOP NUMBER BADGE (VERY LEFT) */}
                    <td style={{ padding: '6px 4px 6px 8px', textAlign: 'center', width: '28px', verticalAlign: 'middle' }}>
                      <span
                        style={{
                          background: isOptimized ? '#22c55e' : '#38bdf8',
                          color: '#0f172a',
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '800',
                          fontSize: '0.7rem',
                          flexShrink: 0
                        }}
                      >
                        {idx + 1}
                      </span>
                    </td>

                    {/* COL 1: TIMING STACK */}
                    <td style={{ padding: '6px 6px', textAlign: 'center', width: '56px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: '500', color: '#94a3b8' }} title="Heure d'arrivée estimée">
                          {arrivalTimeFormatted}
                        </span>
                        <span style={{ fontSize: '0.66rem', color: '#94a3b8' }} title="Temps de service">
                          {serviceMins}m
                        </span>
                        <span style={{ fontSize: '0.7rem', fontWeight: '500', color: '#64748b' }} title="Heure de départ du stop">
                          {departureTimeFormatted}
                        </span>
                      </div>
                    </td>

                    {/* COL 2: REAL TIME WINDOWS COLUMN */}
                    <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'middle', width: '90px' }}>
                      {renderStopRealTimeWindows(stop, arrivalTimeFormatted)}
                    </td>

                    {/* COL 3: CLIENT & ADDRESS */}
                    <td style={{ padding: '6px 6px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                        {/* 1. Stop Code */}
                        <span
                          style={{
                            fontSize: '0.68rem',
                            color: '#94a3b8',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={stop.stop_number || `STOP-${stop.id || (idx + 1)}`}
                        >
                          {stop.stop_number || `STOP-${stop.id || (idx + 1)}`}
                        </span>

                        {/* 2. Client Name */}
                        <strong
                          style={{
                            color: '#ffffff',
                            fontSize: '0.76rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '160px',
                            display: 'inline-block'
                          }}
                          title={stop.client_name}
                        >
                          {stop.client_name}
                        </strong>

                        {/* 3. Address */}
                        <div
                          style={{
                            fontSize: '0.68rem',
                            color: '#94a3b8',
                            marginTop: '1px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '180px'
                          }}
                          title={`${stop.address || ''}, ${stop.city || ''}`}
                        >
                          {stop.address}, {stop.city}
                        </div>
                      </div>
                    </td>

                    {/* COL 4: WEIGHT & VOLUME */}
                    <td style={{ padding: '6px 6px', textAlign: 'right', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '0.72rem', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                          {stop.type === 'pickup' ? '+' : '-'}{Math.round(stop.weight_kg || 0)} kg
                        </span>
                        <span style={{ fontSize: '0.72rem', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                          {stop.type === 'pickup' ? '+' : '-'}{stop.volume_m3 || 0.5} m³
                        </span>
                      </div>
                    </td>

                  </tr>

                  {/* INTER-STOP TRAJET ROW */}
                  {idx < stops.length - 1 && (() => {
                    const nextStop = stops[idx + 1];
                    const km = nextStop.leg_distance_km !== undefined
                      ? nextStop.leg_distance_km
                      : calcHaversineDistanceKm(stop.latitude, stop.longitude, nextStop.latitude, nextStop.longitude);
                    let mins = nextStop.leg_drive_mins !== undefined
                      ? nextStop.leg_drive_mins
                      : Math.max(1, Math.round((km / 40) * 60));
                    if (nextStop.leg_drive_mins === undefined && stop.estimated_arrival && nextStop.estimated_arrival) {
                      const currArrSec = timeStringToSec(stop.estimated_arrival);
                      const windowStartSec = getStopWindowStartSec(stop);
                      const serviceStartSec = (windowStartSec !== null && currArrSec < windowStartSec) ? windowStartSec : currArrSec;
                      const nextArrSec = timeStringToSec(nextStop.estimated_arrival);
                      const serviceSec = (parseInt(stop.service_time_mins || 10)) * 60;
                      // Next stop's pauses (if any) are already included in nextArrSec, so subtract them
                      const nextPauses = (pauses || []).filter(p => p.before_stop_id === nextStop.id || parseInt(p.before_stop_id) === parseInt(nextStop.id));
                      const nextPauseSec = nextPauses.reduce((sum, p) => sum + parseInt(p.duration_mins || p.estimated_length_mins || 10) * 60, 0);
                      const driveSec = nextArrSec - nextPauseSec - (serviceStartSec + serviceSec);
                      if (driveSec > 0) {
                        mins = Math.max(1, Math.round(driveSec / 60));
                      }
                    }

                    return (
                      <tr key={`leg-${stop.id || idx}`} style={{ background: '#0f172a' }}>
                        <td colSpan={5} style={{ padding: '4px 10px', borderTop: '1px dashed #334155', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                            <Truck size={13} color="#94a3b8" />
                            <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{mins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{km} km</span></span>
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* TRAJET END: LAST STOP TO END HUB */}
      {stops.length > 0 && (() => {
        const lastStop = stops[stops.length - 1];
        const km = job?.return_leg_distance_km !== undefined
          ? job.return_leg_distance_km
          : calcHaversineDistanceKm(lastStop.latitude, lastStop.longitude, endHub?.latitude, endHub?.longitude);
        let mins = job?.return_leg_drive_mins !== undefined
          ? job.return_leg_drive_mins
          : Math.max(1, Math.round((km / 40) * 60));
        if (job?.return_leg_drive_mins === undefined && lastStop.estimated_arrival && returnTimeStr) {
          const lastArrSec = timeStringToSec(lastStop.estimated_arrival);
          const windowStartSec = getStopWindowStartSec(lastStop);
          const serviceStartSec = (windowStartSec !== null && lastArrSec < windowStartSec) ? windowStartSec : lastArrSec;
          const serviceSec = (parseInt(lastStop.service_time_mins || 10)) * 60;
          const retSec = timeStringToSec(returnTimeStr);
          const driveSec = retSec - (serviceStartSec + serviceSec);
          if (driveSec > 0) {
            mins = Math.max(1, Math.round(driveSec / 60));
          }
        }
        return (
          <div style={{ background: '#0f172a', padding: '4px 10px', borderTop: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'left' }}>
            <Truck size={13} color="#94a3b8" />
            <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{mins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{km} km</span></span>
          </div>
        );
      })()}

      {/* RETOUR FOOTER BLOCK */}
      <div style={{ background: '#0f172a', padding: '6px 10px', borderTop: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: '#94a3b8' }}>
        <Building size={13} color="#94a3b8" />
        <span>Retour :</span>
        <span style={{ fontSize: '0.72rem', color: '#38bdf8', fontWeight: '700' }}>{returnTimeStr}</span>
        <span style={{ padding: '2px 6px', fontSize: '0.72rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', borderRadius: '4px', marginLeft: 'auto' }}>
          {endHubName}
        </span>
      </div>
    </div>
  );
}

export default function JobOptimizationModal({
  isOpen,
  onClose,
  previewData,
  onApplyOptimization
}) {
  const [activeTab, setActiveTab] = useState('comparison');
  const [isApplying, setIsApplying] = useState(false);

  if (!isOpen || !previewData) return null;

  const {
    jobId,
    currentJob,
    currentStops = [],
    currentPauses = [],
    optimizedJob,
    optimizedStops = [],
    optimizedPauses = [],
    unassignedStops = [],
    savings = {},
    orderedStopIds = [],
    unassignedStopIds = [],
    recommendedStartTime,
    startHub,
    endHub
  } = previewData;

  const handleApply = async () => {
    setIsApplying(true);
    if (onApplyOptimization) {
      await onApplyOptimization(jobId, orderedStopIds, unassignedStopIds, recommendedStartTime, previewData?.pauses || []);
    }
    setIsApplying(false);
    onClose();
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="overlay-card" style={{ width: '960px', maxWidth: '96vw', padding: '0', overflow: 'hidden', background: '#0f172a', border: '1px solid #334155', borderRadius: '12px' }}>
        
        {/* MODAL HEADER */}
        <div style={{ background: '#1e293b', padding: '16px 24px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} color="#38bdf8" />
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#f8fafc' }}>
                Optimisation de la Mission : <span>{currentJob.display_name || `Mission #${jobId}`}</span>
              </h3>
            </div>
          </div>

          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        {/* SAVINGS SUMMARY DIFFERENCE BANNER */}
        <div style={{ background: '#0f172a', padding: '10px 24px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Différence :
            </span>

            {recommendedStartTime && recommendedStartTime !== (currentJob.estimated_departure || '07:30').slice(0,5) && (
              <div style={{ background: 'rgba(56, 189, 248, 0.15)', border: '1px solid #38bdf8', padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', color: '#38bdf8', fontWeight: '700' }}>
                💡 Départ suggéré: {recommendedStartTime}
              </div>
            )}

            {unassignedStops.length > 0 && (
              <div style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', padding: '3px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={14} color="#ef4444" />
                <span style={{ fontSize: '0.75rem', color: '#fca5a5', fontWeight: '700' }}>
                  {unassignedStops.length} stop(s) non inséré(s)
                </span>
              </div>
            )}
          </div>

          {/* STATS RIGHT-ALIGNED IDENTICAL TO MAIN VIEW */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title="Gain / Différence de distance">
              <Navigation size={13} color="#22c55e" />
              <span style={{
                fontSize: '0.8rem',
                fontWeight: '700',
                color: savings.km > 0 ? '#22c55e' : savings.km === 0 ? '#cbd5e1' : '#ef4444'
              }}>
                {savings.km > 0 ? `-${savings.km} km` : savings.km === 0 ? `0.0 km` : `+${Math.abs(savings.km)} km`}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title="Gain / Différence de temps">
              <Clock size={13} color="#38bdf8" />
              <span style={{
                fontSize: '0.8rem',
                fontWeight: '700',
                color: savings.durationSec > 0 ? '#22c55e' : savings.durationSec === 0 ? '#cbd5e1' : '#ef4444'
              }}>
                {savings.durationSec > 0 ? `-${formatSecToHoursMins(savings.durationSec)}` : savings.durationSec === 0 ? `0 min` : `+${formatSecToHoursMins(Math.abs(savings.durationSec))}`}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title="Gain / Différence de coût">
              <DollarSign size={13} color="#ef4444" />
              <span style={{
                fontSize: '0.8rem',
                fontWeight: '700',
                color: savings.cost > 0 ? '#22c55e' : savings.cost === 0 ? '#cbd5e1' : '#ef4444'
              }}>
                {savings.cost > 0 ? `-${savings.cost.toFixed(2)} CHF` : savings.cost === 0 ? `0.00 CHF` : `+${Math.abs(savings.cost).toFixed(2)} CHF`}
              </span>
            </div>
          </div>
        </div>

        {/* NAVIGATION SUB-TABS */}
        <div style={{ display: 'flex', background: '#1e293b', padding: '0 24px', borderBottom: '1px solid #334155' }}>
          <button
            className={`db-sub-tab ${activeTab === 'comparison' ? 'active' : ''}`}
            style={{ padding: '10px 16px', fontSize: '0.82rem', borderBottom: activeTab === 'comparison' ? '2px solid #38bdf8' : 'none' }}
            onClick={() => setActiveTab('comparison')}
          >
            Comparaison des parcours (Actuel vs Optimisé)
          </button>

          <button
            className={`db-sub-tab ${activeTab === 'dropped' ? 'active' : ''}`}
            style={{ padding: '10px 16px', fontSize: '0.82rem', color: unassignedStops.length > 0 ? '#ef4444' : '#94a3b8', borderBottom: activeTab === 'dropped' ? '2px solid #ef4444' : 'none' }}
            onClick={() => setActiveTab('dropped')}
          >
            Stops non insérés ({unassignedStops.length})
          </button>
        </div>

        {/* MODAL CONTENT BODY */}
        <div style={{ padding: '20px 24px', maxHeight: '58vh', overflowY: 'auto' }}>
          
          {/* TAB 1: COMPARISON VIEW */}
          {activeTab === 'comparison' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              
              {/* LEFT COLUMN: CURRENT JOB */}
              <SingleRouteCard
                title="Parcours Actuel"
                isOptimized={false}
                job={currentJob}
                stops={currentStops}
                pauses={previewData?.currentPauses || []}
                startHub={startHub}
                endHub={endHub}
              />

              {/* RIGHT COLUMN: OPTIMIZED JOB */}
              <SingleRouteCard
                title="Parcours Optimisé VROOM"
                isOptimized={true}
                job={optimizedJob}
                stops={optimizedStops}
                pauses={previewData?.pauses || []}
                startHub={startHub}
                endHub={endHub}
              />

            </div>
          )}

          {/* TAB 2: DROPPED / UNASSIGNED STOPS VIEW */}
          {activeTab === 'dropped' && (
            <div>
              {unassignedStops.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                  <CheckCircle2 size={36} color="#22c55e" style={{ marginBottom: '8px' }} />
                  <h4>Tous les stops respectent les contraintes d'horaires et de capacités !</h4>
                  <p style={{ fontSize: '0.8rem' }}>Aucun stop n'a dû être retiré de la tournée.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', padding: '10px 14px', borderRadius: '6px', fontSize: '0.8rem', color: '#fca5a5' }}>
                    <strong>Attention :</strong> Ces stops ne peuvent pas être livrés dans leurs créneaux horaires requis par ce véhicule. Ils seront replacés dans la liste <i>"Livraisons à planifier"</i> lors de l'application.
                  </div>

                  {unassignedStops.map(s => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', border: '1px solid #ef4444', padding: '12px 16px', borderRadius: '8px' }}>
                      <div>
                        <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#f8fafc' }}>{s.client_name} (Stop #{s.id})</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.address}, {s.city}</div>
                        <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: '4px', fontWeight: '600' }}>
                          ⚠️ Raison: {s.reason || 'Créneau horaire non respecté ou capacité véhicule dépassée'}
                        </div>
                      </div>

                      <div style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700' }}>
                        Non inséré
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* MODAL FOOTER ACTIONS */}
        <div style={{ background: '#1e293b', padding: '14px 24px', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            className="btn-action"
            style={{ padding: '8px 16px', fontSize: '0.82rem', background: 'transparent', color: '#94a3b8', borderColor: '#475569' }}
            onClick={onClose}
            disabled={isApplying}
          >
            Annuler
          </button>

          <button
            className="btn-action"
            style={{ padding: '8px 20px', fontSize: '0.82rem', background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={handleApply}
            disabled={isApplying}
          >
            <Check size={16} />
            <span>{isApplying ? 'Application en cours...' : "Appliquer l'optimisation"}</span>
          </button>
        </div>

      </div>
    </div>
  );
}
